/**
 * ──────────────────────────────────────────────────────────────────────────────
 *  PeriscoPe x IsAlbi · Commerce Engineering · 兆輝
 * ──────────────────────────────────────────────────────────────────────────────
 *  This file is part of the IsAlbi B2B Shopify implementation.
 * ──────────────────────────────────────────────────────────────────────────────
 *  Enhanced product form with automatic stock type property assignment.
 *  When items are added to cart, automatically calculates and assigns stock type
 *  properties (_stock_type, _dispatch_window_start, _dispatch_window_end,
 *  _forward_delivery_date) based on variant metafields (inventory.eta) and
 *  inventory quantities. Ensures all cart items have proper stock classification
 *  for ERP/Patchworks integration.
 * ──────────────────────────────────────────────────────────────────────────────
 */

function isIsalbiAtcTimingLogEnabled() {
  try {
    if (window.__isalbiCartAddSync && typeof window.__isalbiCartAddSync.isLogEnabled === 'function') {
      return window.__isalbiCartAddSync.isLogEnabled();
    }
    return (
      window.__ISALBI_CART_SYNC_LOG__ === true ||
      window.__ISALBI_CART_PERF__ === true ||
      (typeof window.location !== 'undefined' && /[?&]cart_(sync_log|perf)=1(?:&|$)/.test(window.location.search))
    );
  } catch (e) {
    return false;
  }
}

function logAtcTiming(phase, detail) {
  if (!isIsalbiAtcTimingLogEnabled()) {
    return;
  }
  const payload = Object.assign({ phase }, detail);
  // eslint-disable-next-line no-console
  console.info('[IsAlbi ProductForm ATC]', JSON.stringify(payload));
}

/**
 * When /cart/add.js returns a full cart payload, skip a follow-up GET /cart.js for analytics/events.
 * Accepts:
 * - top-level `items` (Cart API default),
 * - nested `cart.items` (some bundled responses / apps),
 * - bare **line item** at the root (legacy / some stores: `variant_id`, `key`, `quantity`, …) — we wrap as `{ ...data, items: [data] }`.
 *   That snapshot may omit cart-level totals when the server omits them; callers that need full cart totals should refetch.
 * Empty `items` is not treated as a usable cart snapshot.
 * @param {object} data
 * @returns {object|null}
 */
function isBareLineItemCartAddPayload(data) {
  if (!data || typeof data !== 'object') {
    return false;
  }
  if (Array.isArray(data.items)) {
    return false;
  }
  if (data.cart && Array.isArray(data.cart.items)) {
    return false;
  }
  if (typeof data.key !== 'string' || data.key.length === 0) {
    return false;
  }
  const vid = Number(data.variant_id);
  if (!Number.isFinite(vid)) {
    return false;
  }
  const qty = Number(data.quantity);
  if (!Number.isFinite(qty)) {
    return false;
  }
  return true;
}

function cartSnapshotFromBareLineItem(data) {
  return Object.assign({}, data, { items: [data] });
}

function pickCartFromAddResponse(data) {
  if (!data) {
    return null;
  }
  if (Array.isArray(data.items) && data.items.length > 0) {
    return data;
  }
  if (data.cart && Array.isArray(data.cart.items) && data.cart.items.length > 0) {
    return data.cart;
  }
  if (isBareLineItemCartAddPayload(data)) {
    return cartSnapshotFromBareLineItem(data);
  }
  return null;
}

/** Cross-instance cache so hover prefetch can warm data for quick-add / ATC without a second Storefront round trip. */
const ISALBI_VARIANT_ATC_SHARED_CACHE = new Map();
const ISALBI_VARIANT_FETCH_IN_FLIGHT_GLOBAL = new Map();
const VARIANT_ATC_CACHE_TTL_MS = 10000;

function peekSharedVariantAtc(variantId) {
  const key = String(variantId);
  const row = ISALBI_VARIANT_ATC_SHARED_CACHE.get(key);
  if (!row) {
    return { hit: false };
  }
  if (Date.now() - row.ts > VARIANT_ATC_CACHE_TTL_MS) {
    ISALBI_VARIANT_ATC_SHARED_CACHE.delete(key);
    return { hit: false };
  }
  return { hit: true, value: row.value };
}

function setSharedVariantAtc(variantId, value) {
  if (value == null) {
    return;
  }
  const key = String(variantId);
  ISALBI_VARIANT_ATC_SHARED_CACHE.set(key, { value, ts: Date.now() });
  if (ISALBI_VARIANT_ATC_SHARED_CACHE.size > 120) {
    const first = ISALBI_VARIANT_ATC_SHARED_CACHE.keys().next().value;
    if (first !== undefined) {
      ISALBI_VARIANT_ATC_SHARED_CACHE.delete(first);
    }
  }
}

/** Periscope variant-stock-type: single client cache + in-flight (TTL aligns with server Redis default ~5m). */
const STOCK_TYPE_CLIENT_CACHE = new Map();
const STOCK_TYPE_CLIENT_IN_FLIGHT = new Map();
const STOCK_TYPE_CLIENT_TTL_MS = 300000;

function peekStockTypeClientCache(variantId) {
  const key = String(variantId);
  const row = STOCK_TYPE_CLIENT_CACHE.get(key);
  if (!row) {
    return { hit: false };
  }
  if (Date.now() - row.ts > STOCK_TYPE_CLIENT_TTL_MS) {
    STOCK_TYPE_CLIENT_CACHE.delete(key);
    return { hit: false };
  }
  return { hit: true, value: row.value };
}

function setStockTypeClientCache(variantId, value) {
  if (value == null) {
    return;
  }
  const key = String(variantId);
  STOCK_TYPE_CLIENT_CACHE.set(key, { value, ts: Date.now() });
  if (STOCK_TYPE_CLIENT_CACHE.size > 200) {
    const first = STOCK_TYPE_CLIENT_CACHE.keys().next().value;
    if (first !== undefined) {
      STOCK_TYPE_CLIENT_CACHE.delete(first);
    }
  }
}

/**
 * App proxy URL for Periscope variant stock type (must stay in sync with fetchStockTypePropertiesWithClientCache).
 * @param {string|number} variantId
 * @param {string} [endpoint='/apps/isalbi-periscope/variant-stock-type']
 */
function buildVariantStockTypeApiUrl(variantId, endpoint = '/apps/isalbi-periscope/variant-stock-type') {
  let apiUrl = `${endpoint}?variantId=${encodeURIComponent(variantId)}`;
  const shopDomain = typeof window !== 'undefined' ? window.Shopify?.shop || null : null;
  if (shopDomain) {
    apiUrl += `&shop=${encodeURIComponent(shopDomain)}`;
  }
  return apiUrl;
}

async function fetchStockTypePropertiesWithClientCache(variantId) {
  const key = String(variantId);
  const peek = peekStockTypeClientCache(variantId);
  if (peek.hit) {
    return peek.value;
  }
  if (STOCK_TYPE_CLIENT_IN_FLIGHT.has(key)) {
    return STOCK_TYPE_CLIENT_IN_FLIGHT.get(key);
  }

  const p = (async () => {
    const endpoints = ['/apps/isalbi-periscope/variant-stock-type'];

    for (let i = 0; i < endpoints.length; i++) {
      const endpoint = endpoints[i];
      try {
        const apiUrl = buildVariantStockTypeApiUrl(variantId, endpoint);

        const response = await fetch(apiUrl);

        if (response.ok) {
          const stock = await response.json();
          if (stock && stock._stock_type) {
            setStockTypeClientCache(variantId, stock);
            return stock;
          }
        } else if (response.status === 404 && i < endpoints.length - 1) {
          continue;
        }
      } catch (error) {
        if (i < endpoints.length - 1) {
          continue;
        }
        console.error(`[ProductForm] Error fetching stock type for variant ${variantId}:`, error); // eslint-disable-line
      }
    }

    return null;
  })();

  STOCK_TYPE_CLIENT_IN_FLIGHT.set(key, p);
  p.finally(() => {
    STOCK_TYPE_CLIENT_IN_FLIGHT.delete(key);
  });
  return p;
}

if (typeof window !== 'undefined') {
  window.__isalbiVariantStockTypeClient = {
    peek: peekStockTypeClientCache,
    fetch: fetchStockTypePropertiesWithClientCache,
    warm: setStockTypeClientCache
  };
}

function shouldSkipNetworkPrefetchHints() {
  try {
    const c = navigator.connection;
    if (c?.saveData) {
      return true;
    }
    const et = c?.effectiveType;
    if (et === 'slow-2g' || et === '2g') {
      return true;
    }
  } catch (e) {
    // ignore
  }
  return false;
}

/** `<link rel=preload as=script>` so first drawer open does not wait on cart-items.js download. */
function preloadCartItemsScriptOnce() {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return;
  }
  if (!window.theme?.scripts?.cartItems) {
    return;
  }
  if (window.__isalbiCartItemsPreloadRequested) {
    return;
  }
  window.__isalbiCartItemsPreloadRequested = true;
  const src = window.theme.scripts.cartItems;
  if (document.querySelector(`script[src="${src}"]`)) {
    return;
  }
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'script';
  link.href = src;
  document.head.appendChild(link);
}

/** Warm GET /cart.js once (HTTP cache) before the ATC parallel batch. */
function warmCartJsFetchOnce() {
  if (typeof window === 'undefined' || !window.theme?.routes?.cart) {
    return;
  }
  if (window.__isalbiCartJsWarmDone) {
    return;
  }
  if (shouldSkipNetworkPrefetchHints()) {
    return;
  }
  window.__isalbiCartJsWarmDone = true;
  fetch(`${window.theme.routes.cart}.js`, {
    method: 'GET',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    }
  }).catch(() => {
    window.__isalbiCartJsWarmDone = false;
  });
}

if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.variantDataCache = new Map();
      this._variantFetchInFlight = new Map();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
        this.bindIntentPrefetch();
      }
    }

    /**
     * Warm variant JSON, Periscope stock-type, and cart snapshot on pointerdown (before click completes)
     * so network overlaps the press; caches dedupe with hover/idle prefetch.
     */
    bindIntentPrefetch() {
      if (!this.submitBtn || !this.form) {
        return;
      }
      if (this._intentPrefetchBound) {
        return;
      }
      this._intentPrefetchBound = true;
      this.submitBtn.addEventListener(
        'pointerdown',
        () => {
          if (shouldSkipNetworkPrefetchHints()) {
            return;
          }
          const raw = this.form.querySelector('[name="id"]')?.value;
          const vid = raw ? parseInt(String(raw).replace(/.*\//, ''), 10) : null;
          if (!vid || Number.isNaN(vid)) {
            return;
          }
          this.getVariantDataCached(vid).catch(() => {});
          this.fetchStockTypeProperties(vid).catch(() => {});
          warmCartJsFetchOnce();
        },
        { passive: true }
      );
    }

    getOverrideMoq() {
      const moqUtils = this.getMoqUtils();
      if (moqUtils && typeof moqUtils.getGlobalOverrideMoq === 'function') {
        return moqUtils.getGlobalOverrideMoq();
      }
      const b2bCheckEl = document.querySelector('.theme__b2b-check');
      if (!b2bCheckEl) return null;
      const raw = b2bCheckEl.dataset.overrideMoq;
      if (!raw) return null;
      const parsed = parseInt(raw, 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    }

    getMoqUtils() {
      return window.__isalbiMoqUtils || null;
    }

    getProductMoqFromVariantData(variantData) {
      const rawMoq = variantData?.product?.metafields?.custom?.minimum_order_quantity?.value
        || variantData?.product?.metafields?.custom?.minimum_order_quantity
        || this.form?.querySelector('[name="quantity"]')?.dataset?.step
        || this.form?.querySelector('[name="quantity"]')?.step
        || this.form?.querySelector('[name="quantity"]')?.min
        || 1;
      const parsed = parseInt(rawMoq, 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    }

    async getVariantDataCached(variantId) {
      const key = String(variantId);
      const now = Date.now();
      const existing = this.variantDataCache.get(key);
      if (existing && (now - existing.ts) < 10000) {
        return existing.value;
      }

      const sharedPeek = peekSharedVariantAtc(variantId);
      if (sharedPeek.hit) {
        this.variantDataCache.set(key, { value: sharedPeek.value, ts: Date.now() });
        return sharedPeek.value;
      }

      if (ISALBI_VARIANT_FETCH_IN_FLIGHT_GLOBAL.has(key)) {
        const value = await ISALBI_VARIANT_FETCH_IN_FLIGHT_GLOBAL.get(key);
        this.variantDataCache.set(key, { value, ts: Date.now() });
        return value;
      }

      if (this._variantFetchInFlight.has(key)) {
        return this._variantFetchInFlight.get(key);
      }

      const promise = (async () => {
        const value = await this.getVariantData(variantId);
        this.variantDataCache.set(key, { value, ts: Date.now() });
        setSharedVariantAtc(variantId, value);
        if (this.variantDataCache.size > 120) {
          const first = this.variantDataCache.keys().next().value;
          if (first !== undefined) this.variantDataCache.delete(first);
        }
        return value;
      })();
      this._variantFetchInFlight.set(key, promise);
      ISALBI_VARIANT_FETCH_IN_FLIGHT_GLOBAL.set(key, promise);
      promise.finally(() => {
        this._variantFetchInFlight.delete(key);
        ISALBI_VARIANT_FETCH_IN_FLIGHT_GLOBAL.delete(key);
      });
      return promise;
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const atcT0 = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
      logAtcTiming('submit-start', { t0: Math.round(atcT0 * 100) / 100 });

      const formData = new FormData(this.form);
      const b2bCheckEl = document.querySelector('.theme__b2b-check');
      if (b2bCheckEl) {
        const companyGid = b2bCheckEl.dataset.companyGid || '';
        const axCompanyId = b2bCheckEl.dataset.axCompanyId || '';
        const sendNameToAx = b2bCheckEl.dataset.sendNameToAx === 'true';
        if (companyGid) formData.append('properties[_company_gid]', companyGid);
        if (axCompanyId) formData.append('properties[_ax_company_id]', axCompanyId);
        formData.append('properties[_send_customer_name_to_ax]', sendNameToAx ? 'true' : 'false');
      }
      
      const variantId = formData.get('id');
      const variantIdNum = variantId ? parseInt(String(variantId).replace(/.*\//, ''), 10) : null;
      const variantIdForProps = variantIdNum;

      let variantDataForMoq = null;
      let oldCartData = null;
      let stockFromApiEarly = null;

      const nowMs = () => (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now());

      // One round-trip window: variant JSON (MOQ + stock fallback), Periscope stock-type, pre-add cart snapshot.
      if (variantIdNum && !isNaN(variantIdNum)) {
        logAtcTiming('early-parallel-start', { msSinceStart: Math.round((nowMs() - atcT0) * 100) / 100 });
        const tEarly0 = nowMs();
        const [vData, stockA, cartJ] = await Promise.all([
          this.getVariantDataCached(variantIdNum),
          this.fetchStockTypeProperties(variantIdNum).catch(() => null),
          fetch(`${theme.routes.cart}.js`).then(async (r) => {
            if (!r.ok) throw new Error(String(r.status));
            return r.json();
          })
        ]);
        variantDataForMoq = vData;
        stockFromApiEarly = stockA;
        oldCartData = cartJ;
        logAtcTiming('early-parallel-done', {
          msSinceStart: Math.round((nowMs() - atcT0) * 100) / 100,
          msParallel: Math.round((nowMs() - tEarly0) * 100) / 100,
          stockApiOk: Boolean(stockA && stockA._stock_type)
        });

        // Safety check: Validate MOQ before submission (non-blocking on fetch failure)
        try {
          if (variantDataForMoq && variantDataForMoq.product?.metafields) {
            const moqUtils = this.getMoqUtils();
            const overrideMoq = this.getOverrideMoq();
            const productMoq = this.getProductMoqFromVariantData(variantDataForMoq);
            const moq = moqUtils && typeof moqUtils.resolveEffectiveMoq === 'function'
              ? moqUtils.resolveEffectiveMoq({ overrideMoq, productMoq, fallback: 1 })
              : (overrideMoq || productMoq || 1);
            const inventoryQty = variantDataForMoq.inventory_quantity || 0;
            const hasEta = variantDataForMoq.product.metafields.custom?.eta != null ||
              variantDataForMoq.metafields?.inventory?.eta != null ||
              variantDataForMoq.metafields?.custom?.eta != null;

            if (
              inventoryQty > 0 &&
              inventoryQty < moq &&
              !hasEta
            ) {
              const errorMsg = theme.strings?.insufficientStockMoq || 'Insufficient stock to meet minimum order quantity';
              this.setErrorMsgState(errorMsg);
              this.submitBtn.removeAttribute('aria-disabled');
              this.submitBtn.classList.remove('is-loading');
              return;
            }
          }
        } catch (error) {
          console.warn('[ProductForm] Could not validate MOQ, continuing:', error); // eslint-disable-line
        }
      } else {
        logAtcTiming('variant-parse-skip', { msSinceStart: Math.round((nowMs() - atcT0) * 100) / 100 });
      }

      logAtcTiming('after-moq', {
        msSinceStart: Math.round((nowMs() - atcT0) * 100) / 100
      });

      // Always normalize requested quantity to the effective MOQ before add-to-cart.
      const moqUtils = this.getMoqUtils();
      const overrideMoq = this.getOverrideMoq();
      const productMoq = this.getProductMoqFromVariantData(variantDataForMoq);
      const effectiveMoq = moqUtils && typeof moqUtils.resolveEffectiveMoq === 'function'
        ? moqUtils.resolveEffectiveMoq({ overrideMoq, productMoq, fallback: 1 })
        : (overrideMoq || productMoq || 1);
      const rawQuantity = parseInt(formData.get('quantity') || '1', 10);
      const quantityInput = this.form?.querySelector('[name="quantity"]');
      const isHiddenQuickAddQuantity =
        quantityInput &&
        String(quantityInput.getAttribute('type') || '').toLowerCase() === 'hidden';
      if (isHiddenQuickAddQuantity) {
        // PLP quick-add hides the quantity input. The server already rendered the
        // correct product MOQ into it; use the higher of that value and the
        // JS-calculated effectiveMoq so a failed calculation can't override a
        // valid server-rendered quantity with the fallback of 1.
        const resolvedQty = Math.max(rawQuantity, effectiveMoq);
        formData.set('quantity', String(resolvedQty));
        quantityInput.value = String(resolvedQty);
      } else if (moqUtils && typeof moqUtils.normalizeQuantityToMoq === 'function') {
        const normalized = moqUtils.normalizeQuantityToMoq(rawQuantity, effectiveMoq);
        formData.set('quantity', String(normalized.quantity));
        if (quantityInput && String(quantityInput.value) !== String(normalized.quantity)) {
          quantityInput.value = String(normalized.quantity);
          quantityInput.dataset.initialValue = String(normalized.quantity);
        }
        if (normalized.adjusted && theme.strings?.quantityAdjustedToMoq) {
          this.setErrorMsgState(theme.strings.quantityAdjustedToMoq.replace('[quantity]', String(normalized.quantity)));
        }
      }
      
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      // Stock line properties (Periscope + fallbacks). Data usually already loaded in early-parallel.
      if (variantIdForProps && !isNaN(variantIdForProps)) {
        let stockTypeProps = stockFromApiEarly;

        if (!stockTypeProps && variantDataForMoq) {
          const props = this.calculateStockTypeProperties(variantDataForMoq);
          stockTypeProps = {
            _stock_type: props.stockType,
            _dispatch_window_start: props.dispatchWindowStart,
            _dispatch_window_end: props.dispatchWindowEnd,
            _forward_delivery_date: props.forwardDeliveryDate || ''
          };
        }

        if (!stockTypeProps) {
          const variantData = await this.getVariantDataCached(variantIdForProps);
          if (variantData) {
            const props = this.calculateStockTypeProperties(variantData);
            stockTypeProps = {
              _stock_type: props.stockType,
              _dispatch_window_start: props.dispatchWindowStart,
              _dispatch_window_end: props.dispatchWindowEnd,
              _forward_delivery_date: props.forwardDeliveryDate || ''
            };
          }
        }

        // Periscope can lag metafields or misclassify; if client metafields say backorder, prefer that over API "available".
        if (stockTypeProps && variantDataForMoq && stockTypeProps._stock_type === 'available') {
          const clientProps = this.calculateStockTypeProperties(variantDataForMoq);
          if (clientProps.stockType === 'backorder') {
            stockTypeProps = {
              _stock_type: 'backorder',
              _dispatch_window_start: clientProps.dispatchWindowStart,
              _dispatch_window_end: clientProps.dispatchWindowEnd,
              _forward_delivery_date: clientProps.forwardDeliveryDate || ''
            };
          }
        }

        // Always add properties if we have them, or use defaults
        if (stockTypeProps) {
          // Verify stock type is valid before adding
          if (stockTypeProps._stock_type !== 'backorder' && stockTypeProps._stock_type !== 'available' && stockTypeProps._stock_type !== 'forward') {
            console.error('[ProductForm] Invalid stock type received:', stockTypeProps._stock_type); // eslint-disable-line
            stockTypeProps = null; // Reset to trigger default fallback
          } else {
            formData.append('properties[_stock_type]', stockTypeProps._stock_type);
            formData.append('properties[_dispatch_window_start]', stockTypeProps._dispatch_window_start);
            formData.append('properties[_dispatch_window_end]', stockTypeProps._dispatch_window_end);
            formData.append('properties[_forward_delivery_date]', stockTypeProps._forward_delivery_date || '');
            this._lastStockTypeProps = stockTypeProps;
          }
        }

        // Default fallback if no stock type data available
        if (!stockTypeProps) {
          const today = new Date().toISOString().split('T')[0];
          formData.append('properties[_stock_type]', 'available');
          formData.append('properties[_dispatch_window_start]', today);
          formData.append('properties[_dispatch_window_end]', today);
          formData.append('properties[_forward_delivery_date]', '');
          this._lastStockTypeProps = { _stock_type: 'available' };
        }
      } else {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        oldCartData = await oldCartResponse.json();
        logAtcTiming('old-cart-only', { msSinceStart: Math.round((nowMs() - atcT0) * 100) / 100 });
      }

      logAtcTiming('stock-props-ready', { msSinceStart: Math.round((nowMs() - atcT0) * 100) / 100 });

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      try {
        logAtcTiming('before-cart-add-post', { msSinceStart: Math.round((nowMs() - atcT0) * 100) / 100 });
        const tCartAdd0 = nowMs();
        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        logAtcTiming('cart-add-response', {
          msSinceStart: Math.round((nowMs() - atcT0) * 100) / 100,
          msCartAddRoundTrip: Math.round((nowMs() - tCartAdd0) * 100) / 100,
          httpOk: response.ok
        });
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        // Check if item was added from Quick Add drawer BEFORE any redirects
        const quickAddDrawer = this.closest('quick-add-drawer');

        // Verify properties were added to the cart item
        if (data.item && data.item.properties) {
          const props = Array.isArray(data.item.properties) 
            ? data.item.properties.reduce((acc, p) => { acc[p.name] = p.value; return acc; }, {})
            : data.item.properties;
          if (!props._stock_type) {
            console.warn('[ProductForm] _stock_type was not added to cart item'); // eslint-disable-line
          } else if (props._stock_type === 'available' && this._lastStockTypeProps && this._lastStockTypeProps._stock_type === 'backorder') {
            console.error('[ProductForm] Stock type mismatch: expected backorder but got available', { // eslint-disable-line
              expected: this._lastStockTypeProps._stock_type,
              actual: props._stock_type,
              variantId: data.variant_id
            });
          }
        } else if (data.items && data.items.length > 0) {
          // Check the items array if item is not directly available
          const addedItem = data.items.find(item => item.variant_id === data.variant_id);
          if (addedItem && addedItem.properties) {
            const props = Array.isArray(addedItem.properties)
              ? addedItem.properties.reduce((acc, p) => { acc[p.name] = p.value; return acc; }, {})
              : addedItem.properties;
            if (!props._stock_type) {
              console.warn('[ProductForm] _stock_type was not added to cart item'); // eslint-disable-line
            } else if (props._stock_type === 'available' && this._lastStockTypeProps && this._lastStockTypeProps._stock_type === 'backorder') {
              console.error('[ProductForm] Stock type mismatch: expected backorder but got available', { // eslint-disable-line
                expected: this._lastStockTypeProps._stock_type,
                actual: props._stock_type,
                variantId: data.variant_id
              });
            }
          }
        }

        // Stock type properties are already added via FormData, no need to update after
        // The backfillStockTypeProperties in cart-items.js will handle any missing properties

        // Update cart icon count.
        ProductForm.updateCartIcon(data);

        // If item was added from Quick Add drawer, show "Added to cart" message.
        if (quickAddDrawer) quickAddDrawer.addedToCart();

        // Authoritative drawer/cart-page sync: section HTML from add response (no fixed 700ms delay).
        const sync = window.__isalbiCartAddSync;
        if (sync && typeof sync.schedulePostAddDrawerUpdate === 'function') {
          sync.schedulePostAddDrawerUpdate({
            cartDrawer: this.cartDrawer,
            data,
            openDrawerAfter: !quickAddDrawer && theme.settings.afterAtc === 'drawer',
            onCartPageRefresh:
              window.location.pathname === theme.routes.cart
                ? () => {
                  const cartItems = document.querySelector('cart-items');
                  if (!cartItems) return;
                  if (cartItems.dataset.empty === 'true') {
                    window.location.reload();
                  } else {
                    return cartItems.refresh();
                  }
                }
                : undefined
          });
        } else if (this.cartDrawer) {
          this.cartDrawer.renderContents(
            data,
            !quickAddDrawer && theme.settings.afterAtc === 'drawer'
          );
        } else if (window.location.pathname === theme.routes.cart) {
          const cartItems = document.querySelector('cart-items');
          if (cartItems) {
            if (cartItems.dataset.empty === 'true') {
              window.location.reload();
            } else {
              cartItems.refresh();
            }
          }
        }

        let newCartData = pickCartFromAddResponse(data);
        if (!newCartData) {
          logAtcTiming('post-add-cart-refetch', {
            reason: 'add-payload-missing-items',
            ...(isIsalbiAtcTimingLogEnabled()
              ? {
                  responseKeys: Object.keys(data).slice(0, 24),
                  hasItem: Boolean(data.item),
                  hasCartItems: Boolean(data.cart && Array.isArray(data.cart.items))
                }
              : {})
          });
          const newCartResponse = await fetch(`${theme.routes.cart}.js`);
          if (!newCartResponse.ok) throw new Error(newCartResponse.status);
          newCartData = await newCartResponse.json();
        } else {
          logAtcTiming('post-add-cart-from-add-response', { skippedRefetch: true });
        }

        const addedVariantId = data.variant_id ?? data.item?.variant_id;
        const addedQuantity = data.quantity ?? data.item?.quantity;
        const oldItems = oldCartData && Array.isArray(oldCartData.items) ? oldCartData.items : [];
        const itemInOldCart = addedVariantId != null
          ? oldItems.filter((item) => item.variant_id === addedVariantId)[0]
          : undefined;

        // Check if product was already in the cart
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: addedVariantId,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === addedQuantity)
                ? itemInOldCart.quantity : addedQuantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: addedVariantId
            }
          }));
        }
      } catch (error) {
        console.error('[ProductForm] Error in handleSubmit:', error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Fetches stock type properties from the API endpoint
     * @param {number} variantId - The variant ID
     * @returns {Promise<Object|null>} - Stock type properties or null
     */
    async fetchStockTypeProperties(variantId) {
      return fetchStockTypePropertiesWithClientCache(variantId);
    }

    /**
     * Gets variant data from the page
     * @param {number} variantId - The variant ID
     * @returns {Object|null} - Variant data or null
     */
    /**
     * Product page / quick-add: resolve Shopify product id for Storefront GraphQL metafield fetch.
     * (Previously only `product-card` had an id — main PDP had no path, so getVariantData was often null.)
     * @returns {string|null}
     */
    _resolveProductIdForStorefrontGraphql() {
      const productCard = this.closest('product-card');
      if (productCard && productCard.dataset.productId) {
        return String(productCard.dataset.productId);
      }
      const jsProduct = this.closest('.js-product');
      if (jsProduct && jsProduct.dataset.productId) {
        return String(jsProduct.dataset.productId);
      }
      const b2bScript = typeof document !== 'undefined' ? document.getElementById('b2b-variant-prices') : null;
      if (b2bScript && b2bScript.textContent) {
        try {
          const j = JSON.parse(b2bScript.textContent);
          if (j.product_id != null) return String(j.product_id);
        } catch (e) {
          /* ignore */
        }
      }
      return null;
    }

    async getVariantData(variantId) {
      // Try multiple approaches to get product data
      let productData = null;
      let variant = null;
      
      // Method 1: Look for product JSON in script tags (works on product pages)
      const scriptTags = document.querySelectorAll('script[type="application/json"]');
      for (const scriptEl of scriptTags) {
        try {
          const parsed = JSON.parse(scriptEl.textContent);
          if (parsed.product && parsed.product.variants && Array.isArray(parsed.product.variants)) {
            productData = parsed;
            break;
          }
        } catch (e) {
          // Not valid JSON or not product data, continue
        }
      }
      
      // Method 2: On collection pages, read MOQ directly from the product-card data attribute (server-rendered)
      if (!productData) {
        const productCard = this.closest('product-card');
        if (productCard) {
          const cardMoq = productCard.dataset.moq;
          if (cardMoq) {
            productData = {
              product: {
                metafields: {
                  custom: { minimum_order_quantity: { value: cardMoq } }
                }
              }
            };
          }
        }
      }
      
      // Method 3: Check if product data is stored in a global object (some themes do this)
      if (!productData && window.productData) {
        productData = window.productData;
      }
      
      // Method 4: If still no product data, resolve product ID (product-card, .js-product PDP, or b2b-variant-prices) and fetch via Storefront API
      // (same approach as base price fetcher)
      if (!productData) {
        const productId = this._resolveProductIdForStorefrontGraphql();
        if (productId) {
          // Use the same Storefront API config as base price fetcher
          const storefrontConfig = window.BasePriceFetcher?.config || {
            storefront_token: window.storefrontAccessToken || 'b4be724bc06f25e98244590d7bd53ee6',
            apiUrl: '/api/2025-10/graphql.json',
            fallbackApiUrl: '/api/2024-07/graphql.json'
          };
          
          const productGid = `gid://shopify/Product/${productId}`;
          // Use the same query structure as base price fetcher (which works)
          const query = `
            query getProductMetafields($id: ID!) {
              product(id: $id) {
                id
                metafields(identifiers: [
                  {namespace: "custom", key: "soh_status"},
                  {namespace: "custom", key: "eta"},
                  {namespace: "custom", key: "minimum_order_quantity"}
                ]) {
                  namespace
                  key
                  value
                }
                variants(first: 250) {
                  edges {
                    node {
                      id
                      availableForSale
                      metafields(identifiers: [
                        {namespace: "custom", key: "soh_status"},
                        {namespace: "inventory", key: "eta"}
                      ]) {
                        namespace
                        key
                        value
                      }
                    }
                  }
                }
              }
            }
          `;
          
          try {
            const response = await fetch(storefrontConfig.apiUrl, {
              method: 'POST',
              headers: {
                'X-Shopify-Storefront-Access-Token': storefrontConfig.storefront_token,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                query,
                variables: { id: productGid }
              })
            });
            
            if (!response.ok) {
              console.warn('[ProductForm] Storefront API response not OK:', response.status, response.statusText); // eslint-disable-line
              // Try fallback API version
              if (storefrontConfig.fallbackApiUrl) {
                const fallbackResponse = await fetch(storefrontConfig.fallbackApiUrl, {
                  method: 'POST',
                  headers: {
                    'X-Shopify-Storefront-Access-Token': storefrontConfig.storefront_token,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({
                    query,
                    variables: { id: productGid }
                  })
                });
                
                if (fallbackResponse.ok) {
                  const fallbackData = await fallbackResponse.json();
                  if (fallbackData.errors) {
                    console.error('[ProductForm] Storefront API GraphQL errors:', fallbackData.errors); // eslint-disable-line
                    return null;
                  }
                  if (fallbackData.data && fallbackData.data.product) {
                    return this._processStorefrontApiResponse(fallbackData.data.product, variantId);
                  }
                }
              }
              return null;
            }
            
            const data = await response.json();
            
            if (data.errors) {
              console.error('[ProductForm] Storefront API GraphQL errors:', data.errors); // eslint-disable-line
              return null;
            }
            
            if (data.data && data.data.product) {
              const result = this._processStorefrontApiResponse(data.data.product, variantId);
              if (result) {
                return result;
              }
            }
            return null;
          } catch (error) {
            console.error('[ProductForm] Error fetching product metafields via Storefront API:', error); // eslint-disable-line
            return null;
          }
        }
      }
      
      if (!productData || !productData.product || !productData.product.variants) {
        return null;
      }
      
      // Find variant by ID - handle both string and number comparisons
      variant = productData.product.variants.find((v) => {
        const vId = typeof v.id === 'string' ? parseInt(v.id, 10) : v.id;
        const searchId = typeof variantId === 'string' ? parseInt(variantId, 10) : variantId;
        return vId === searchId || v.id === variantId || v.id === variantId.toString();
      });
      
      if (!variant) {
        return null;
      }
      
      const productMetafields = productData.product.metafields || {};
      
      return {
        metafields: variant.metafields || {},
        inventory_quantity: variant.inventory_quantity || 0,
        product: {
          metafields: productMetafields
        }
      };
    }

    /**
     * Processes Storefront API response and converts it to our expected format
     * @param {Object} product - Product object from Storefront API
     * @param {number} variantId - The variant ID to find
     * @returns {Object|null} - Variant data or null
     */
    _processStorefrontApiResponse(product, variantId) {
      // Convert GraphQL response to our expected format
      const productMetafields = {};
      if (product.metafields) {
        product.metafields.forEach(mf => {
          // Storefront API can return null metafields if they don't exist
          if (!mf || !mf.namespace || !mf.key) {
            return;
          }
          if (!productMetafields[mf.namespace]) {
            productMetafields[mf.namespace] = {};
          }
          productMetafields[mf.namespace][mf.key] = { value: mf.value };
        });
      }
      
      // Find the variant - handle GID format
      const variantNode = product.variants.edges.find(edge => {
        const vId = edge.node.id.replace('gid://shopify/ProductVariant/', '');
        const vIdNum = parseInt(vId, 10);
        const searchId = typeof variantId === 'string' ? parseInt(variantId, 10) : variantId;
        return vId === variantId.toString() || vIdNum === searchId || vId === searchId.toString();
      });
      
      if (!variantNode) {
        return null;
      }
      
      const variant = variantNode.node;
      const variantMetafields = {};
      if (variant.metafields) {
        variant.metafields.forEach(mf => {
          // Storefront API can return null metafields if they don't exist
          if (!mf || !mf.namespace || !mf.key) {
            return;
          }
          if (!variantMetafields[mf.namespace]) {
            variantMetafields[mf.namespace] = {};
          }
          variantMetafields[mf.namespace][mf.key] = { value: mf.value };
        });
      }
      
      
      // Storefront API doesn't provide inventoryQuantity, so we set it to 0
      // Stock type calculation will rely on metafields (soh_status, eta) instead
      return {
        metafields: variantMetafields,
        inventory_quantity: 0, // Storefront API doesn't expose inventory quantity
        product: {
          metafields: productMetafields
        }
      };
    }

    /**
     * Calculates stock type properties for a variant
     * @param {Object} variantData - Variant data with metafields and inventory_quantity
     * @returns {Object} - Stock type properties
     */
    calculateStockTypeProperties(variantData) {
      const c = typeof window !== 'undefined' && window.__isalbiStockTypeClassification;
      if (!c || typeof c.extractClassificationInputsFromStorefrontVariant !== 'function' || typeof c.classifyBaseStockType !== 'function') {
        console.warn('[ProductForm] stock-type-classification.js not loaded; using available + today'); // eslint-disable-line
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        return {
          stockType: 'available',
          dispatchWindowStart: todayStr,
          dispatchWindowEnd: todayStr,
          forwardDeliveryDate: ''
        };
      }

      const inputs = c.extractClassificationInputsFromStorefrontVariant(variantData);
      const baseStockType = c.classifyBaseStockType(inputs);
      const eta = inputs.eta;

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

      let dispatchWindowStart = todayStr;
      let dispatchWindowEnd = todayStr;

      if (baseStockType === 'backorder' && eta) {
        const etaDate = new Date(eta);
        etaDate.setHours(0, 0, 0, 0);
        const endDate = new Date(etaDate);
        endDate.setDate(endDate.getDate() + 2);
        dispatchWindowStart = `${etaDate.getFullYear()}-${String(etaDate.getMonth() + 1).padStart(2, '0')}-${String(etaDate.getDate()).padStart(2, '0')}`;
        dispatchWindowEnd = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
      }

      return {
        stockType: baseStockType,
        dispatchWindowStart,
        dispatchWindowEnd,
        forwardDeliveryDate: ''
      };
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);

  /**
   * Warm shared variant cache from a collection `product-card` (hover prefetch). Uses the same
   * Storefront path as add-to-cart. Deduped via ISALBI_VARIANT_FETCH_IN_FLIGHT_GLOBAL.
   * @param {HTMLElement} productCard
   * @param {number} variantId
   * @returns {Promise<unknown>}
   */
  function primeVariantDataCacheFromProductCard(productCard, variantId) {
    const key = String(variantId);
    const peek = peekSharedVariantAtc(variantId);
    if (peek.hit) {
      return Promise.resolve(peek.value);
    }
    if (ISALBI_VARIANT_FETCH_IN_FLIGHT_GLOBAL.has(key)) {
      return ISALBI_VARIANT_FETCH_IN_FLIGHT_GLOBAL.get(key);
    }

    const proxy = Object.create(ProductForm.prototype);
    proxy.closest = function closest(sel) {
      return sel === 'product-card' ? productCard : null;
    };

    const p = (async () => {
      const value = await ProductForm.prototype.getVariantData.call(proxy, variantId);
      setSharedVariantAtc(variantId, value);
      return value;
    })();

    ISALBI_VARIANT_FETCH_IN_FLIGHT_GLOBAL.set(key, p);
    p.finally(() => {
      ISALBI_VARIANT_FETCH_IN_FLIGHT_GLOBAL.delete(key);
    });
    return p;
  }

  /**
   * Prefetch variant JSON on hover (desktop pointer only): debounced, idle-scheduled, skips save-data / 2G.
   * Does not block clicks; failed prefetch is ignored (ATC still loads on submit).
   */
  (function initIsalbiAtcHoverPrefetch() {
    function run() {
    if (typeof window === 'undefined' || typeof theme === 'undefined') {
      return;
    }
    if (!theme.device?.hasHover || !theme.mediaMatches?.md) {
      return;
    }

    function shouldSkipPrefetch() {
      try {
        const c = navigator.connection;
        if (c?.saveData) {
          return true;
        }
        const et = c?.effectiveType;
        if (et === 'slow-2g' || et === '2g') {
          return true;
        }
      } catch (e) {
        // ignore
      }
      return false;
    }

    function runWhenIdle(fn) {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(fn, { timeout: 2500 });
      } else {
        setTimeout(fn, 0);
      }
    }

    const debounceMs = 140;
    /** One idle prefetch (variant + Periscope stock-type) per variant id; caches prevent duplicate network. */
    const prefetchScheduledVariantKeys = new Set();

    function debounceObserver(fn, wait) {
      let t;
      return function debounced(...args) {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
    }

    function schedulePrefetch(productCard) {
      const vid = parseInt(productCard.dataset.variantId, 10);
      if (!vid || Number.isNaN(vid)) {
        return;
      }
      const k = String(vid);
      if (prefetchScheduledVariantKeys.has(k)) {
        return;
      }
      preloadCartItemsScriptOnce();
      prefetchScheduledVariantKeys.add(k);

      // Start Periscope stock-type as soon as we commit to prefetch (not deferred to idle), so TLS/DNS + Redis
      // work overlaps with the idle-scheduled variant JSON fetch. Same guards as below; deduped in-flight + client cache.
      const ranEarlyStockPrefetch = !shouldSkipPrefetch();
      if (ranEarlyStockPrefetch) {
        fetchStockTypePropertiesWithClientCache(vid).catch(() => {
          prefetchScheduledVariantKeys.delete(k);
        });
      }

      runWhenIdle(() => {
        if (shouldSkipPrefetch()) {
          prefetchScheduledVariantKeys.delete(k);
          return;
        }
        const tasks = [primeVariantDataCacheFromProductCard(productCard, vid)];
        if (!ranEarlyStockPrefetch) {
          tasks.push(fetchStockTypePropertiesWithClientCache(vid));
        }
        Promise.all(tasks).catch(() => {
          prefetchScheduledVariantKeys.delete(k);
        });
      });
    }

    function bindQuickAddPrefetch(button) {
      if (button.dataset.isalbiAtcPrefetchBound === 'true') {
        return;
      }
      button.dataset.isalbiAtcPrefetchBound = 'true';
      let hoverTimer = null;
      button.addEventListener('mouseenter', () => {
        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(() => {
          const card = button.closest('product-card');
          if (card && card.dataset.variantId) {
            schedulePrefetch(card);
          }
        }, debounceMs);
      });
      button.addEventListener('mouseleave', () => {
        clearTimeout(hoverTimer);
      });
    }

      document.querySelectorAll('.js-quick-add').forEach(bindQuickAddPrefetch);

      const cartIcon = document.getElementById('cart-icon');
      if (cartIcon && cartIcon.dataset.isalbiAtcWarmBound !== 'true') {
        cartIcon.dataset.isalbiAtcWarmBound = 'true';
        let cartIconTimer = null;
        cartIcon.addEventListener('mouseenter', () => {
          clearTimeout(cartIconTimer);
          cartIconTimer = setTimeout(() => {
            if (shouldSkipNetworkPrefetchHints()) {
              return;
            }
            preloadCartItemsScriptOnce();
            warmCartJsFetchOnce();
          }, 200);
        });
        cartIcon.addEventListener('mouseleave', () => {
          clearTimeout(cartIconTimer);
        });
      }

    if (typeof MutationObserver !== 'undefined') {
      const root = document.getElementById('main-content') || document.body;
      const onMut = debounceObserver((mutations) => {
        mutations.forEach((m) => {
          m.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              node.querySelectorAll?.('.js-quick-add').forEach(bindQuickAddPrefetch);
            }
          });
        });
      }, 300);
      const obs = new MutationObserver(onMut);
      obs.observe(root, { childList: true, subtree: true });
    }
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run, { once: true });
    } else {
      run();
    }
  })();

  /**
   * PLP/PDP: batch-warm Periscope stock-type + variant JSON on requestIdleCallback (with timeout),
   * using page DOM (product cards / data-variant-id) so work starts early without hover.
   * Skips save-data / 2G; throttles batch size on 3g. Deduped via existing caches + prefetchedIds for infinite scroll.
   */
  (function initIsalbiPageIdleBatchPrefetch() {
    const PLP_TYPES = ['collection', 'search'];
    const PDP_TYPES = ['product'];
    const MAX_PLP_CARDS = 48;
    const MAX_PDP_VARIANTS = 64;
    const BATCH_CONCURRENCY = 4;
    const IDLE_TIMEOUT_MS = 4500;
    const INCREMENTAL_DEBOUNCE_MS = 650;

    const prefetchedVariantIds = new Set();

    function getPageType() {
      if (typeof theme !== 'undefined' && theme.pageType) {
        return theme.pageType;
      }
      const p = window.location.pathname;
      if (p.includes('/products/')) {
        return 'product';
      }
      if (p.includes('/collections/')) {
        return 'collection';
      }
      if (p === '/search' || p.startsWith('/search')) {
        return 'search';
      }
      return '';
    }

    function shouldSkipPrefetch() {
      try {
        const c = navigator.connection;
        if (c?.saveData) {
          return true;
        }
        const et = c?.effectiveType;
        if (et === 'slow-2g' || et === '2g') {
          return true;
        }
      } catch (e) {
        // ignore
      }
      return false;
    }

    /** Smaller batches on 3g to avoid competing with the rest of the page. */
    function isLikelySlowConnection() {
      try {
        return navigator.connection?.effectiveType === '3g';
      } catch (e) {
        return false;
      }
    }

    function runWhenIdle(fn) {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(fn, { timeout: IDLE_TIMEOUT_MS });
      } else {
        setTimeout(fn, 0);
      }
    }

    function parseVariantIdAttr(raw) {
      if (!raw || raw === '') {
        return null;
      }
      const n = parseInt(String(raw).replace(/^gid:\/\/shopify\/ProductVariant\//, ''), 10);
      if (!Number.isFinite(n) || n <= 0) {
        return null;
      }
      return n;
    }

    function collectPlpCardRows() {
      const root = document.getElementById('main-content') || document.body;
      const cards = Array.from(root.querySelectorAll('product-card[data-variant-id]'));
      const cap = isLikelySlowConnection() ? Math.min(12, MAX_PLP_CARDS) : MAX_PLP_CARDS;
      const out = [];
      const seen = new Set();
      for (let i = 0; i < cards.length && out.length < cap; i++) {
        const card = cards[i];
        const vid = parseInt(card.dataset.variantId, 10);
        if (!vid || Number.isNaN(vid) || seen.has(vid)) {
          continue;
        }
        seen.add(vid);
        out.push({ card, vid });
      }
      return out;
    }

    function collectPdpVariantIds() {
      const root = document.getElementById('main-content') || document.body;
      const cap = isLikelySlowConnection() ? 8 : MAX_PDP_VARIANTS;
      const out = [];
      const seen = new Set();
      root.querySelectorAll('[data-variant-id]').forEach((el) => {
        if (out.length >= cap) {
          return;
        }
        const v = parseVariantIdAttr(el.getAttribute('data-variant-id'));
        if (v === null || seen.has(v)) {
          return;
        }
        seen.add(v);
        out.push(v);
      });
      return out;
    }

    async function runChunkedParallel(rows, runOne) {
      for (let i = 0; i < rows.length; i += BATCH_CONCURRENCY) {
        const slice = rows.slice(i, i + BATCH_CONCURRENCY);
        await Promise.all(slice.map((row) => runOne(row).catch(() => null)));
      }
    }

    async function runPlpBatch(rows) {
      if (rows.length === 0) {
        return;
      }
      preloadCartItemsScriptOnce();
      warmCartJsFetchOnce();
      await runChunkedParallel(rows, ({ card, vid }) =>
        Promise.all([
          fetchStockTypePropertiesWithClientCache(vid),
          primeVariantDataCacheFromProductCard(card, vid)
        ])
      );
    }

    async function runPdpBatch(ids, productForm) {
      if (ids.length === 0 || !productForm) {
        return;
      }
      preloadCartItemsScriptOnce();
      warmCartJsFetchOnce();
      await runChunkedParallel(ids, (vid) =>
        Promise.all([
          fetchStockTypePropertiesWithClientCache(vid),
          ProductForm.prototype.getVariantDataCached.call(productForm, vid)
        ])
      );
    }

    function markPrefetched(rowsOrIds) {
      if (typeof rowsOrIds[0] === 'number') {
        rowsOrIds.forEach((id) => prefetchedVariantIds.add(String(id)));
      } else {
        rowsOrIds.forEach(({ vid }) => prefetchedVariantIds.add(String(vid)));
      }
    }

    /** Scan all cards (not only the first N) so infinite-scroll appended rows are discoverable. */
    function collectNewPlpRowsOnly() {
      const root = document.getElementById('main-content') || document.body;
      const cards = Array.from(root.querySelectorAll('product-card[data-variant-id]'));
      const out = [];
      const seen = new Set();
      for (let i = 0; i < cards.length; i++) {
        const card = cards[i];
        const vid = parseInt(card.dataset.variantId, 10);
        if (!vid || Number.isNaN(vid) || seen.has(vid)) {
          continue;
        }
        seen.add(vid);
        if (prefetchedVariantIds.has(String(vid))) {
          continue;
        }
        out.push({ card, vid });
      }
      return out;
    }

    function kickIdleBatch() {
      if (typeof window === 'undefined' || typeof theme === 'undefined') {
        return;
      }
      if (window.__ISALBI_TEST__) {
        return;
      }
      const pt = getPageType();
      if (!PLP_TYPES.includes(pt) && !PDP_TYPES.includes(pt)) {
        return;
      }
      if (shouldSkipPrefetch()) {
        return;
      }

      runWhenIdle(() => {
        if (shouldSkipPrefetch()) {
          return;
        }
        if (PLP_TYPES.includes(pt)) {
          const rows = collectPlpCardRows();
          markPrefetched(rows);
          runPlpBatch(rows).catch(() => {});
        } else if (PDP_TYPES.includes(pt)) {
          const form = document.querySelector('product-form');
          const ids = collectPdpVariantIds();
          markPrefetched(ids);
          runPdpBatch(ids, form).catch(() => {});
        }
      });
    }

    function bindPlpIncrementalObserver() {
      if (typeof MutationObserver === 'undefined') {
        return;
      }
      const root = document.getElementById('main-content') || document.body;
      let t = null;
      const obs = new MutationObserver(() => {
        clearTimeout(t);
        t = setTimeout(() => {
          if (shouldSkipPrefetch()) {
            return;
          }
          const newRows = collectNewPlpRowsOnly();
          if (newRows.length === 0) {
            return;
          }
          const cap = isLikelySlowConnection() ? 8 : 24;
          const slice = newRows.slice(0, cap);
          markPrefetched(slice);
          runWhenIdle(() => {
            if (shouldSkipPrefetch()) {
              return;
            }
            runPlpBatch(slice).catch(() => {});
          });
        }, INCREMENTAL_DEBOUNCE_MS);
      });
      obs.observe(root, { childList: true, subtree: true });
    }

    if (document.readyState === 'loading') {
      document.addEventListener(
        'DOMContentLoaded',
        () => {
          kickIdleBatch();
          if (PLP_TYPES.includes(getPageType())) {
            bindPlpIncrementalObserver();
          }
        },
        { once: true }
      );
    } else {
      kickIdleBatch();
      if (PLP_TYPES.includes(getPageType())) {
        bindPlpIncrementalObserver();
      }
    }
  })();
}

// Test hook for override detection (company override MOQ)
if (typeof window !== 'undefined' && window.__ISALBI_TEST__) {
  window.__isalbiDiscountTestHooks = {
    isSpecialDiscountCustomer: () => {
      const el = document.querySelector('.theme__b2b-check');
      if (!el) return false;
      if (el.dataset.overrideMoq) return true;
      return !!(el.dataset.isStaffDiscount === 'true');
    }
  };
  window.__isalbiProductFormTestHooks = {
    pickCartFromAddResponse
  };
}
